import React, {
	useEffect,
	useState,
	memo,
	useMemo,
	useCallback,
	useRef,
} from 'react';
import { usePageTitle } from 'utils/pageTitle';
import { useForm } from 'react-hook-form';
import { Link, useHistory, useLocation } from 'react-router-dom';
import AdminLayout from 'pages/_rd_dashboard/AdminLayout';
import Spinner from 'components/Spinner';
import Paginator from 'components/Paginator';
import StatCard from 'components/StatCard/StatCard';
import Sparkline from 'components/Sparkline/Sparkline';
import FormAjaxSelect from 'components/Form/FormAjaxSelect';
import ThroughputStrip from 'pages/_rd_dashboard/QueueMonitor/components/ThroughputStrip';
import PerQueueTable from 'pages/_rd_dashboard/QueueMonitor/components/PerQueueTable';
import FailuresByCauseTable from 'pages/_rd_dashboard/QueueMonitor/components/FailuresByCauseTable';
import RetryHotspots from 'pages/_rd_dashboard/QueueMonitor/components/RetryHotspots';
import WowCard from 'pages/_rd_dashboard/QueueMonitor/components/WowCard';
import PerSourceHourlyPanel from 'pages/_rd_dashboard/QueueMonitor/components/PerSourceHourlyPanel';
import HealthBanner from 'pages/_rd_dashboard/QueueMonitor/components/HealthBanner';
import WorkersPanel from 'pages/_rd_dashboard/QueueMonitor/components/WorkersPanel';
import FailureAnomalyChart from 'pages/_rd_dashboard/QueueMonitor/components/FailureAnomalyChart';
import CapacityTrend from 'pages/_rd_dashboard/QueueMonitor/components/CapacityTrend';
import EmailOutboxPanel from 'pages/_rd_dashboard/QueueMonitor/components/EmailOutboxPanel';
import {
	EM_DASH,
	formatNumber,
	formatCapAware,
	formatEtDateTime,
} from 'pages/_rd_dashboard/QueueMonitor/components/queueMonitorFormat';
import { useQueueMonitor } from 'data/repositories/queueMonitorRepository';
import { showToast } from 'data/reducers/toastReducer';
import { useDispatch } from 'react-redux';
import { openModal } from 'data/reducers/modalReducer';
import { MODAL_TYPES } from 'view/ModalManager';
import './QueueMonitor.css';

// Failed-jobs window selector options (hours). Replaces the old
// FAILED_JOB_RETRY_HOURS = 36000 hardcode.
const FAILED_WINDOW_OPTIONS = [24, 72, 168, 720];
const DEFAULT_FAILED_WINDOW_HOURS = 168;

// Bulk retry server-side cap (v2 contract 5.2); the rendered N and the sent
// limit are always min(N, BULK_RETRY_MAX) - the true-bound rule.
const BULK_RETRY_MAX = 500;

// v3 capacity-trend window (days). Contract 5.2 allows {30, 90} only.
const CAPACITY_DAY_OPTIONS = [30, 90];
const DEFAULT_CAPACITY_DAYS = 30;

// v3 anomaly window (hours). The failures-by-hour anomaly chart defaults to the
// trailing 7 days, matching the contract /anomalies default.
const ANOMALY_WINDOW_HOURS = 168;

// ============================================
// Deep-linkable URL state (v2 contract 14.2)
// Defaults are OMITTED from the URL so default views keep a clean path;
// invalid values fall back SILENTLY (a garbage link renders the default
// dashboard, never crashes). Length limits mirror the BE validation rules.
// ============================================

const TAB_OPTIONS = ['overview', 'pending', 'processing', 'stuck', 'failed', 'outbox'];
// The tabs that list jobs, and so take the property and queue filters.
const JOB_LIST_TABS = ['pending', 'processing', 'stuck', 'failed'];
const MAX_QUEUE_PARAM_LENGTH = 32;
const MAX_EXCEPTION_PARAM_LENGTH = 191;

const parseUrlState = (search) => {
	const params = new URLSearchParams(search || '');
	const tab = params.get('tab');
	const queue = params.get('queue');
	const hours = parseInt(params.get('hours'), 10);
	const exception = params.get('exception');
	return {
		tab: TAB_OPTIONS.includes(tab) ? tab : 'overview',
		queue: queue && queue.length <= MAX_QUEUE_PARAM_LENGTH ? queue : null,
		hours: FAILED_WINDOW_OPTIONS.includes(hours)
			? hours
			: DEFAULT_FAILED_WINDOW_HOURS,
		exception:
			exception && exception.length <= MAX_EXCEPTION_PARAM_LENGTH
				? exception
				: null,
	};
};

const buildUrlSearch = ({ tab, queue, hours, exception }) => {
	const params = new URLSearchParams();
	if (tab && tab !== 'overview') params.set('tab', tab);
	if (queue) params.set('queue', queue);
	if (hours && hours !== DEFAULT_FAILED_WINDOW_HOURS) {
		params.set('hours', String(hours));
	}
	if (exception) params.set('exception', exception);
	const search = params.toString();
	return search ? `?${search}` : '';
};

// ============================================
// Series helpers (5-min rollup points from /stats "series")
// Points are SPARSE - never assume fixed spacing.
// ============================================

const parsePointTs = (point) => {
	const ts = Date.parse(point?.ts);
	return Number.isNaN(ts) ? null : ts;
};

const sliceLast24h = (points) => {
	if (!Array.isArray(points) || points.length === 0) {
		return [];
	}
	const latestTs = parsePointTs(points[points.length - 1]);
	if (latestTs === null) {
		return [];
	}
	const cutoff = latestTs - 24 * 60 * 60 * 1000;
	return points.filter((p) => {
		const ts = parsePointTs(p);
		return ts !== null && ts >= cutoff;
	});
};

// Pending delta vs ~1h ago (most recent point at least one hour older
// than the latest point). Returns a StatCard delta shape or null.
const buildPendingDelta = (points) => {
	if (!Array.isArray(points) || points.length < 2) {
		return null;
	}
	const latest = points[points.length - 1];
	const latestTs = parsePointTs(latest);
	if (latestTs === null || typeof latest.pending_total !== 'number') {
		return null;
	}
	const hourAgo = latestTs - 60 * 60 * 1000;
	let reference = null;
	for (let i = points.length - 1; i >= 0; i -= 1) {
		const candidate = points[i];
		const ts = parsePointTs(candidate);
		if (
			ts !== null &&
			ts <= hourAgo &&
			typeof candidate.pending_total === 'number'
		) {
			reference = candidate;
			break;
		}
	}
	if (!reference || reference.pending_total === 0) {
		return null;
	}
	const diff = latest.pending_total - reference.pending_total;
	const pct = Math.round((diff / reference.pending_total) * 100);
	const direction = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';
	return {
		text: `${diff > 0 ? '+' : ''}${pct}% vs 1h ago`,
		direction,
		// For pending, down is good.
		good: direction === 'flat' ? null : direction === 'down',
	};
};

// True-failures delta: last 24h of per-bucket failure counts vs prior 24h.
// Only rendered when the series actually covers part of the prior window.
const buildFailuresDelta = (points) => {
	if (!Array.isArray(points) || points.length === 0) {
		return null;
	}
	const latestTs = parsePointTs(points[points.length - 1]);
	if (latestTs === null) {
		return null;
	}
	const dayMs = 24 * 60 * 60 * 1000;
	const last24Start = latestTs - dayMs;
	const prior24Start = latestTs - 2 * dayMs;
	let last24 = 0;
	let prior24 = 0;
	let hasPriorCoverage = false;
	points.forEach((point) => {
		const ts = parsePointTs(point);
		if (ts === null || typeof point.failures !== 'number') {
			return;
		}
		if (ts > last24Start) {
			last24 += point.failures;
		} else if (ts > prior24Start) {
			prior24 += point.failures;
			hasPriorCoverage = true;
		}
	});
	if (!hasPriorCoverage) {
		return null;
	}
	const diff = last24 - prior24;
	const direction = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';
	const text =
		prior24 > 0
			? `${diff > 0 ? '+' : ''}${Math.round((diff / prior24) * 100)}% vs prior 24h`
			: `${diff > 0 ? '+' : ''}${diff.toLocaleString('en-US')} vs prior 24h`;
	return {
		text,
		direction,
		// For failures, down is good.
		good: direction === 'flat' ? null : direction === 'down',
	};
};

// ============================================
// Queue state tiles (contract 6.11)
// ============================================

const StatsGrid = memo(({ summary, onNavigateTab }) => {
	if (!summary) return null;

	const stats = summary.summary || {};
	const failedSummary = summary.failed_summary || {};
	const points = summary.series?.points || [];
	const latestPoint = points.length > 0 ? points[points.length - 1] : null;
	const last24hPoints = sliceLast24h(points);

	// Pending tile prefers the latest REAL sampled count over the capped
	// legacy field; falls back to the legacy field on old BE.
	const pendingValue =
		latestPoint && typeof latestPoint.pending_total === 'number'
			? formatNumber(latestPoint.pending_total)
			: formatCapAware(stats.totalPending);
	const pendingSpark = last24hPoints
		.map((p) => p.pending_total)
		.filter((v) => typeof v === 'number');
	const pendingDelta = buildPendingDelta(points);

	const failuresTrue = failedSummary.failed_last_24h_true;
	const failuresSpark = last24hPoints
		.map((p) => p.failures)
		.filter((v) => typeof v === 'number');
	const failuresDelta = buildFailuresDelta(points);

	const deadLetterTotal =
		failedSummary.dead_letter_total !== undefined
			? failedSummary.dead_letter_total
			: failedSummary.total_failed;

	return (
		<div className="queue-stats-grid mb-4">
			<StatCard
				title="Pending"
				value={pendingValue}
				icon="bi bi-hourglass-split"
				className="stat-primary"
				spark={pendingSpark.length > 0 ? pendingSpark : undefined}
				delta={pendingDelta || undefined}
				tooltip="Jobs waiting to run. Down or holding near zero is good; growth across multiple ticks is bad."
			/>
			<StatCard
				title="Processing"
				value={formatCapAware(stats.totalProcessing)}
				icon="bi bi-arrow-repeat"
				className="stat-info"
				tooltip="Jobs a worker is running right now."
			/>
			<div
				className="stat-card-link"
				role="button"
				tabIndex={0}
				onClick={() => onNavigateTab('stuck')}
				onKeyDown={(e) => {
					if (e.key === 'Enter') onNavigateTab('stuck');
				}}
			>
				<StatCard
					title="Stuck (>2h)"
					value={formatNumber(
						stats.stuckJobsCount !== undefined ? stats.stuckJobsCount : 0,
					)}
					icon="bi bi-exclamation-triangle"
					className="stat-warning"
					tooltip="Jobs reserved by a worker for over 2 hours - usually a dead worker. Anything above 0 needs a look. Click to inspect."
				/>
			</div>
			<StatCard
				title="Failures (24h, UTC)"
				value={formatNumber(failuresTrue)}
				icon="bi bi-x-octagon"
				className="stat-secondary"
				spark={failuresSpark.length > 0 ? failuresSpark : undefined}
				delta={failuresDelta || undefined}
				tooltip="True failure count from id deltas - immune to the 30-minute retry churn. Lower is better."
			/>
			<div
				className="stat-card-link"
				role="button"
				tabIndex={0}
				onClick={() => onNavigateTab('failed')}
				onKeyDown={(e) => {
					if (e.key === 'Enter') onNavigateTab('failed');
				}}
			>
				<StatCard
					title="Dead-letter (needs triage)"
					value={formatNumber(
						deadLetterTotal !== undefined ? deadLetterTotal : 0,
					)}
					icon="bi bi-archive"
					className="stat-danger"
					tooltip={`Rows parked in failed_jobs awaiting retry or triage, oldest ${
						failedSummary.dead_letter_oldest_at
							? `${formatEtDateTime(failedSummary.dead_letter_oldest_at)} ET`
							: 'n/a'
					}. This number should shrink. Click to triage.`}
				/>
			</div>
			{failedSummary.awaiting_action && (
				<div
					className="stat-card-link"
					role="button"
					tabIndex={0}
					onClick={() => onNavigateTab('failed')}
					onKeyDown={(e) => {
						if (e.key === 'Enter') onNavigateTab('failed');
					}}
				>
					{/* v2 contract 6.2: sixth tile, renders ONLY when the BE serves
					    the awaiting_action block (old-BE skew -> the v1 five tiles). */}
					<StatCard
						title="Failures awaiting action"
						value={formatNumber(
							failedSummary.awaiting_action.total !== undefined
								? failedSummary.awaiting_action.total
								: null,
						)}
						icon="bi bi-inboxes"
						className="stat-danger"
						tooltip={`Dead-letter failures (all retries exhausted) still parked and waiting for retry or delete. Event-sourced and churn-proof, counted since ${
							failedSummary.awaiting_action.events_coverage_from
								? `${formatEtDateTime(
										failedSummary.awaiting_action.events_coverage_from,
									)} ET`
								: 'n/a'
						}. This number should go to zero. Click to triage.`}
					/>
				</div>
			)}
		</div>
	);
});

StatsGrid.displayName = 'StatsGrid';

// ============================================
// BIS vs Open Data today (extended stats, new BE only)
// ============================================

const BySourceTodayTable = memo(({ rows, grainNote }) => {
	if (!Array.isArray(rows) || rows.length === 0) return null;

	return (
		<div className="analytics-card">
			<h5>BIS vs Open Data today</h5>
			<table className="table table-sm mb-1">
				<thead>
					<tr>
						<th>Source</th>
						<th className="text-end">Executions</th>
						<th className="text-end">Failures</th>
						<th className="text-end">Failure %</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((row) => (
						<tr key={row.job_type}>
							<td>{row.job_type}</td>
							<td className="text-end">{formatNumber(row.executions)}</td>
							<td className="text-end">{formatNumber(row.failures)}</td>
							<td className="text-end">
								{row.failure_pct === null || row.failure_pct === undefined
									? EM_DASH
									: `${row.failure_pct}%`}
							</td>
						</tr>
					))}
				</tbody>
			</table>
			<div className="text-muted small">
				{grainNote || 'daily grain, parent jobs only'}
			</div>
		</div>
	);
});

BySourceTodayTable.displayName = 'BySourceTodayTable';

// ============================================
// Extended stats / property analytics section
// ============================================

const ExtendedStatsGrid = memo(
	({
		extendedStats,
		topPropertiesByJobs,
		isLoadingTopProperties,
		metricsHourly,
		isLoadingMetricsHourly,
		capacity,
		isLoadingCapacity,
		capacityDays,
		onCapacityDaysChange,
	}) => {
		if (!extendedStats) return null;

		const jobsPerPropertyTooltip = extendedStats.formula_note
			? `${extendedStats.formula_note}${
					extendedStats.expected_runs_note
						? `\n${extendedStats.expected_runs_note}`
						: ''
				}`
			: undefined;

		const failures14d = Array.isArray(extendedStats.failures_last_14d)
			? extendedStats.failures_last_14d
			: null;
		const failures14dPoints = failures14d
			? failures14d
					.map((d) => d.failures)
					.filter((v) => typeof v === 'number')
			: [];

		const hasAnalyticsRow =
			Array.isArray(extendedStats.by_source_today) ||
			(failures14d && failures14d.length > 0) ||
			!!extendedStats.wow_daily ||
			!!extendedStats.wow_hourly;

		return (
			<div className="extended-stats-section mb-4">
				<h3 className="mb-3">Extended Statistics</h3>
				<div className="row g-3">
					<div className="col-12 col-sm-6 col-lg-4 col-xxl-2">
						<StatCard
							title="Total Properties"
							value={extendedStats.total_properties || 0}
							icon="bi bi-buildings"
							className="stat-primary"
						/>
					</div>
					<div className="col-12 col-sm-6 col-lg-4 col-xxl-2">
						<StatCard
							title="Successful Addresses"
							value={extendedStats.successful_addresses || 0}
							icon="bi bi-check-circle"
							className="stat-success"
						/>
					</div>
					<div className="col-12 col-sm-6 col-lg-4 col-xxl-2">
						<StatCard
							title="Job runs per property (today)"
							value={extendedStats.avg_jobs_per_property || 0}
							icon="bi bi-bar-chart"
							className="stat-warning"
							tooltip={jobsPerPropertyTooltip}
						/>
					</div>
					<div className="col-12 col-sm-6 col-lg-4 col-xxl-2">
						<StatCard
							title="Total Jobs Today"
							value={extendedStats.jobs_today || 0}
							icon="bi bi-calendar-day"
							className="stat-secondary"
						/>
					</div>
					<div className="col-12 col-sm-6 col-lg-4 col-xxl-2">
						<StatCard
							title="Unique Job Data Sets"
							value={extendedStats.unique_job_types || 0}
							icon="bi bi-collection"
							className="stat-purple"
						/>
					</div>
				</div>

				{hasAnalyticsRow && (
					<div className="row g-3 mt-1">
						{Array.isArray(extendedStats.by_source_today) && (
							<div className="col-12 col-lg-4">
								<BySourceTodayTable
									rows={extendedStats.by_source_today}
									grainNote={extendedStats.grain_note}
								/>
							</div>
						)}
						{failures14d && failures14d.length > 0 && (
							<div className="col-12 col-lg-4">
								<div className="analytics-card">
									<h5>Failures last 14 days</h5>
									<Sparkline
										points={failures14dPoints}
										width={260}
										height={48}
										stroke="#dc3545"
										ariaLabel="failures per day, last 14 days"
									/>
									<div className="text-muted small">
										{extendedStats.grain_note ||
											'daily grain, parent jobs only'}
									</div>
								</div>
							</div>
						)}
						{(extendedStats.wow_daily || extendedStats.wow_hourly) && (
							<div className="col-12 col-lg-4">
								<WowCard
									wow={extendedStats.wow_daily}
									wowHourly={extendedStats.wow_hourly}
								/>
							</div>
						)}
					</div>
				)}

				{/* v2 contract 7.3: per-source hourly error-rate panel; the
				    component renders nothing on null data (old BE skew). */}
				{metricsHourly && (
					<div className="row g-3 mt-1">
						<div className="col-12">
							<PerSourceHourlyPanel
								data={metricsHourly}
								isLoading={!!isLoadingMetricsHourly}
							/>
						</div>
					</div>
				)}

				{/* v3 contract 5.2/5.3: 30/90-day capacity trend. Renders the
				    shell + toggle even on a pre-v3 backend (honest empty state). */}
				<div className="row g-3 mt-1">
					<div className="col-12">
						<CapacityTrend
							data={capacity}
							windowDays={capacityDays}
							onDaysChange={onCapacityDaysChange}
							isLoading={!!isLoadingCapacity}
						/>
					</div>
				</div>

				<div className="row g-3 mt-1">
					<div className="col-12">
						<RetryHotspots
							topProperties={topPropertiesByJobs?.topProperties}
							isLoading={!!isLoadingTopProperties}
						/>
					</div>
				</div>
			</div>
		);
	},
);

ExtendedStatsGrid.displayName = 'ExtendedStatsGrid';

const PropertyAddressFilter = memo(
	({ value, onPropertySelect, onClear, disabled, dispatch }) => {
		const { actions } = useQueueMonitor();

		const [defaultProperties, setDefaultProperties] = useState([]);

		const { control, watch } = useForm({
			defaultValues: {
				property: value?.id || '',
			},
		});

		const watchProeprty = watch('property');

		const loadProperties = useCallback(
			async (query, firstAttempt = false) => {
				if ((!query || query.length < 2) && !firstAttempt) {
					return [];
				}

				try {
					const response = await actions.searchProperties({ query });
					return response?.data || [];
				} catch (error) {
					console.error('Failed to load properties:', error);
					return [];
				}
			},
			[actions],
		);

		useEffect(() => {
			const fetchProperties = async () => {
				const properties = await loadProperties('', true);
				setDefaultProperties(properties);
			};
			fetchProperties();
		}, [loadProperties]);

		const handlePropertyChange = useCallback(
			(propertyId) => {
				if (!propertyId) {
					onClear();
					return;
				}

				// Find the property from the loaded options
				const property = {
					id: propertyId,
				};
				onPropertySelect(property);
			},
			[onPropertySelect, onClear],
		);
		useEffect(() => {
			handlePropertyChange(watchProeprty);
			// eslint-disable-next-line no-use-before-define
		}, [handlePropertyChange, watchProeprty]);

		return (
			<div className="property-address-filter mb-4">
				<div className="filter-header mb-2">
					<h4>Filter by Property</h4>
					{value && (
						<button
							onClick={onClear}
							disabled={disabled}
							className="btn btn-sm btn-outline-secondary"
						>
							<i className="bi bi-x-circle me-1" />
							Clear Filter
						</button>
					)}
				</div>

				<FormAjaxSelect
					name="property"
					control={control}
					loadOptions={loadProperties}
					options={defaultProperties}
					placeholder="Search by address, BIN, block, or lot..."
					valueAccessor={(option) => option.id}
					labelAccessor={(option) => option.label || option.address}
					minChars={2}
					debounceMs={300}
					clearable={true}
					disabled={disabled}
					onChange={handlePropertyChange}
					className="property-search-select"
				/>

				{value && (
					<div className="selected-property-badge mt-2">
						<div className="property-info">
							<i className="bi bi-geo-alt-fill me-2 text-primary" />
							<div>
								<div className="property-address">{value.address}</div>
								<div className="property-meta text-muted small">
									ID: {value.id} | BIN: {value.bin || 'N/A'} | Block/Lot:{' '}
									{value.block}/{value.lot}
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
		);
	},
);

PropertyAddressFilter.displayName = 'PropertyAddressFilter';

const PropertyAddressCell = memo(
	({ propertyId, propertyAddress, onSelectProperty }) => {
		const dispatch = useDispatch();
		if (!propertyId || !propertyAddress) {
			return <span className="text-muted">—</span>;
		}

		const handleOpenStatsModal = (e) => {
			e.preventDefault();
			dispatch(
				openModal(MODAL_TYPES.PROPERTY_STATS, {
					property: {
						id: propertyId,
						address: propertyAddress,
					},
				}),
			);
		};

		return (
			<div className="property-address-cell">
				<button
					onClick={handleOpenStatsModal}
					className="btn btn-link p-0 property-link text-start"
					title="View property statistics"
				>
					{propertyAddress}
				</button>
				<Link
					to={`#`}
					onClick={(e) => {
						e.preventDefault();
						if (onSelectProperty) {
							onSelectProperty({ id: propertyId, address: propertyAddress });
						}
					}}
					className="property-link"
					target="_blank"
					rel="noopener noreferrer"
				>
					{propertyAddress}
				</Link>
				<Link
					to={`/property/overview/${propertyAddress}`}
					className="property-link"
					target="_blank"
					rel="noopener noreferrer"
				>
					<span>
						<i className="bi bi-box-arrow-up-right"></i>
					</span>
				</Link>
				<div className="property-id-badge">
					<span className="badge bg-light text-dark">ID: {propertyId}</span>
				</div>
			</div>
		);
	},
);

PropertyAddressCell.displayName = 'PropertyAddressCell';

const PendingTab = memo(
	({
		jobs,
		pagination,
		onPageChange,
		onDelete,
		isLoading,
		onSelectProperty,
	}) => {
		if (isLoading) {
			return (
				<div className="text-center py-5">
					<Spinner />
				</div>
			);
		}

		if (!jobs || jobs.length === 0) {
			return <p className="text-muted">No pending jobs found</p>;
		}

		return (
			<div className="pending-tab">
				<div className="mb-3">
					<strong>Total Pending Jobs: {pagination?.total || 0}</strong>
				</div>
				<div className="table-responsive">
					<table className="table table-striped">
						<thead>
							<tr>
								<th>ID</th>
								<th>Queue</th>
								<th>Job Class</th>
								<th>Property Address</th>
								<th>Created At</th>
								<th>Waiting</th>
								<th>Attempts</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{jobs.map((job) => (
								<tr key={job.id}>
									<td>{job.id}</td>
									<td>
										<span className="badge bg-secondary">{job.queue}</span>
									</td>
									<td
										className="job-class"
										dangerouslySetInnerHTML={{
											__html: job.jobClass,
										}}
									></td>
									<td>
										<PropertyAddressCell
											propertyId={job.propertyId}
											propertyAddress={job.propertyAddress}
											onSelectProperty={onSelectProperty}
										/>
									</td>
									<td>{job.createdAt}</td>
									<td>{job.waitingDuration}</td>
									<td>{job.attempts}</td>
									<td>
										<button
											className="btn btn-sm btn-danger"
											onClick={() => onDelete(job.id)}
										>
											Delete
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
				{pagination && pagination.lastPage > 1 && (
					<div className="mt-3">
						<Paginator
							currentPage={pagination.currentPage}
							lastPage={pagination.lastPage}
							setPage={onPageChange}
							totalPages={pagination.lastPage}
						/>
					</div>
				)}
			</div>
		);
	},
);

PendingTab.displayName = 'PendingTab';

const ProcessingTab = memo(
	({
		jobs,
		pagination,
		onPageChange,
		onRelease,
		onDelete,
		isLoading,
		onSelectProperty,
	}) => {
		if (isLoading) {
			return (
				<div className="text-center py-5">
					<Spinner />
				</div>
			);
		}

		if (!jobs || jobs.length === 0) {
			return <p className="text-muted">No processing jobs found</p>;
		}

		return (
			<div className="processing-tab">
				<div className="mb-3">
					<strong>Total Processing Jobs: {pagination?.total || 0}</strong>
				</div>
				<div className="table-responsive">
					<table className="table table-striped">
						<thead>
							<tr>
								<th>ID</th>
								<th>Queue</th>
								<th>Job Class</th>
								<th>Property Address</th>
								<th>Reserved At</th>
								<th>Duration</th>
								<th>Attempts</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{jobs.map((job) => (
								<tr key={job.id}>
									<td>{job.id}</td>
									<td>
										<span className="badge bg-info">{job.queue}</span>
									</td>
									<td
										className="job-class"
										dangerouslySetInnerHTML={{
											__html: job.jobClass,
										}}
									></td>
									<td>
										<PropertyAddressCell
											propertyId={job.propertyId}
											propertyAddress={job.propertyAddress}
											onSelectProperty={onSelectProperty}
										/>
									</td>
									<td>{job.reservedAt}</td>
									<td>{job.processingDuration}</td>
									<td>{job.attempts}</td>
									<td>
										<button
											className="btn btn-sm btn-warning me-2"
											onClick={() => onRelease(job.id)}
										>
											Release
										</button>
										<button
											className="btn btn-sm btn-danger"
											onClick={() => onDelete(job.id)}
										>
											Delete
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
				{pagination && pagination.lastPage > 1 && (
					<div className="mt-3">
						<Paginator
							currentPage={pagination.currentPage}
							lastPage={pagination.lastPage}
							setPage={onPageChange}
							totalPages={pagination.lastPage}
						/>
					</div>
				)}
			</div>
		);
	},
);

ProcessingTab.displayName = 'ProcessingTab';

const StuckTab = memo(
	({
		jobs,
		pagination,
		onPageChange,
		onRelease,
		onDelete,
		isLoading,
		onSelectProperty,
	}) => {
		if (isLoading) {
			return (
				<div className="text-center py-5">
					<Spinner />
				</div>
			);
		}

		if (!jobs || jobs.length === 0) {
			return <p className="text-muted">No stuck jobs found</p>;
		}

		return (
			<div className="stuck-tab">
				<div className="mb-3">
					<strong>Total Stuck Jobs: {pagination?.total || 0}</strong>
					<span className="text-muted ms-2">(Processing &gt; 2 hours)</span>
				</div>
				<div className="table-responsive">
					<table className="table table-striped">
						<thead>
							<tr>
								<th>ID</th>
								<th>Queue</th>
								<th>Job Class</th>
								<th>Property Address</th>
								<th>Reserved At</th>
								<th>Duration (hours)</th>
								<th>Attempts</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{jobs.map((job) => (
								<tr key={job.id}>
									<td>{job.id}</td>
									<td>
										<span className="badge bg-info">{job.queue}</span>
									</td>
									<td
										className="job-class"
										dangerouslySetInnerHTML={{
											__html: job.jobClass,
										}}
									></td>
									<td>
										<PropertyAddressCell
											propertyId={job.propertyId}
											propertyAddress={job.propertyAddress}
											onSelectProperty={onSelectProperty}
										/>
									</td>
									<td>{job.reservedAt}</td>
									<td className="text-danger">{job.durationHours}</td>
									<td>{job.attempts}</td>
									<td>
										<button
											className="btn btn-sm btn-warning me-2"
											onClick={() => onRelease(job.id)}
										>
											Release
										</button>
										<button
											className="btn btn-sm btn-danger"
											onClick={() => onDelete(job.id)}
										>
											Delete
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
				{pagination && pagination.lastPage > 1 && (
					<div className="mt-3">
						<Paginator
							currentPage={pagination.currentPage}
							lastPage={pagination.lastPage}
							setPage={onPageChange}
							totalPages={pagination.lastPage}
						/>
					</div>
				)}
			</div>
		);
	},
);

StuckTab.displayName = 'StuckTab';

const FailedTab = memo(
	({
		jobs,
		pagination,
		onPageChange,
		onRetry,
		onDelete,
		isLoading,
		failedSummary,
		onSelectProperty,
		selectedExceptionClass,
	}) => {
		if (isLoading) {
			return (
				<div className="text-center py-5">
					<Spinner />
				</div>
			);
		}

		const parkedTotal =
			failedSummary?.dead_letter_total !== undefined
				? failedSummary.dead_letter_total
				: failedSummary?.total_failed;
		const trueFailures24h = failedSummary?.failed_last_24h_true;

		const header = (
			<div className="mb-3">
				<strong>
					Parked failed jobs (awaiting retry or dead):{' '}
					{formatNumber(parkedTotal !== undefined ? parkedTotal : 0)}
					<i
						className="bi bi-info-circle ms-1"
						title="Rows parked in failed_jobs awaiting retry or triage. queue:retry-failed-jobs re-queues retryable rows every 30 minutes and deletes them from this list, so parked counts are lower bounds on true failures."
					/>
				</strong>
				<span className="text-muted ms-3">
					True failures (24h, UTC): {formatNumber(trueFailures24h)}
				</span>
				<span className="text-muted ms-3">
					Showing: {pagination?.total || 0} (filtered)
				</span>
				{selectedExceptionClass && (
					<span className="badge bg-info ms-3">
						Exception filter: {selectedExceptionClass}
					</span>
				)}
			</div>
		);

		if (!jobs || jobs.length === 0) {
			return (
				<div className="failed-tab">
					{header}
					<p className="text-muted">No failed jobs in window</p>
				</div>
			);
		}

		return (
			<div className="failed-tab">
				{header}
				<div className="table-responsive">
					<table className="table table-striped">
						<thead>
							<tr>
								<th>UUID</th>
								<th>Queue</th>
								<th>Job Class</th>
								<th>Property Address</th>
								<th>Failed At</th>
								<th>Exception</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{jobs.map((job) => (
								<tr key={job.id}>
									<td className="font-monospace small">
										{job.id.substring(0, 8)}...
									</td>
									<td>
										<span className="badge bg-info">{job.queue}</span>
									</td>
									<td
										className="job-class"
										dangerouslySetInnerHTML={{
											__html: job.jobClass,
										}}
									></td>
									<td>
										<PropertyAddressCell
											propertyId={job.propertyId}
											propertyAddress={job.propertyAddress}
											onSelectProperty={onSelectProperty}
										/>
									</td>
									<td>{job.failedAt}</td>
									<td className="exception-preview small">
										{job.exceptionPreview}
									</td>
									<td>
										<button
											className="btn btn-sm btn-success me-2"
											onClick={() => onRetry(job.id)}
										>
											Retry
										</button>
										<button
											className="btn btn-sm btn-danger"
											onClick={() => onDelete(job.id)}
										>
											Delete
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
				{pagination && pagination.lastPage > 1 && (
					<div className="mt-3">
						<Paginator
							currentPage={pagination.currentPage}
							lastPage={pagination.lastPage}
							setPage={onPageChange}
							totalPages={pagination.lastPage}
						/>
					</div>
				)}
			</div>
		);
	},
);

FailedTab.displayName = 'FailedTab';

const QueueMonitor = () => {
	usePageTitle('Admin | Queue Monitor');
	const dispatch = useDispatch();
	const history = useHistory();
	const location = useLocation();

	// URL state is read ONCE on mount (v2 contract 14.2); invalid params fall
	// back silently to defaults. URL writes happen only inside user-event
	// handlers via history.replace - auto-refresh cycles never touch the URL.
	const [initialUrlState] = useState(() => parseUrlState(location.search));

	const [activeTab, setActiveTab] = useState(initialUrlState.tab);
	const [autoRefresh, setAutoRefresh] = useState(false);
	const [selectedProperty, setSelectedProperty] = useState(null);
	const [failedWindowHours, setFailedWindowHours] = useState(
		initialUrlState.hours,
	);
	const [selectedExceptionClass, setSelectedExceptionClass] = useState(
		initialUrlState.exception,
	);
	// ONE shared queue selection across the four job-list tabs (contract 14.1).
	const [selectedQueue, setSelectedQueue] = useState(initialUrlState.queue);
	const [isBulkRetrying, setIsBulkRetrying] = useState(false);
	// v3 capacity-trend window toggle (30/90 days).
	const [capacityDays, setCapacityDays] = useState(DEFAULT_CAPACITY_DAYS);

	// Counts 30s auto-refresh ticks; extended stats join at 60s cadence.
	const refreshTickRef = useRef(0);

	// /failures/breakdown probe flag (contract 4.3, decision 19): ONE failed
	// probe flips this session to the v1 by-exception fallback for this and
	// subsequent cycles; re-probed on full page remount only.
	const breakdownUnsupportedRef = useRef(false);

	// Pagination states for each tab
	const [pendingPage, setPendingPage] = useState(1);
	const [processingPage, setProcessingPage] = useState(1);
	const [stuckPage, setStuckPage] = useState(1);
	const [failedPage, setFailedPage] = useState(1);

	const {
		// Summary & Health
		summary,
		isLoadingSummary,
		health,

		// Extended stats + analytics
		extendedStats,
		topPropertiesByJobs,
		isLoadingTopPropertiesByJobs,

		// Failures by exception class (v1 fallback source)
		failedByException,
		isLoadingFailedByException,

		// Failures breakdown (v2 event-sourced when covered)
		failuresBreakdown,
		isLoadingFailuresBreakdown,

		// Per-source hourly metrics (v2, job_metrics)
		metricsHourly,
		isLoadingMetricsHourly,

		// Failure anomalies (v3, job_metrics hourly). The chart renders nothing
		// while null, so no separate loading flag is consumed.
		anomalies,

		// Capacity trend (v3, job_metrics daily 30/90d)
		capacity,
		isLoadingCapacity,

		// Email outbox (the snapshot the outbox health rules read); null on a
		// backend without the endpoint, which hides the tab
		outbox,

		// Pending Jobs
		pendingJobs,
		pendingJobsPagination,
		isLoadingPendingJobs,

		// Processing Jobs
		processingJobs,
		processingJobsPagination,
		isLoadingProcessingJobs,

		// Stuck Jobs
		stuckJobs,
		stuckJobsPagination,
		isLoadingStuckJobs,

		// Failed Jobs
		failedJobs,
		failedJobsPagination,
		isLoadingFailedJobs,

		// Actions
		actions,
		authToken,
	} = useQueueMonitor();

	const propertyFilterId = useMemo(
		() => selectedProperty?.id || null,
		[selectedProperty],
	);

	// Extract pagination metadata
	const pendingPagination = useMemo(
		() => pendingJobsPagination,
		[pendingJobsPagination],
	);

	const processingPagination = useMemo(
		() => processingJobsPagination,
		[processingJobsPagination],
	);

	const stuckPagination = useMemo(
		() => stuckJobsPagination,
		[stuckJobsPagination],
	);

	const failedPagination = useMemo(
		() => failedJobsPagination,
		[failedJobsPagination],
	);

	// Extract job data arrays
	const pendingJobsData = useMemo(
		() => pendingJobs?.data || pendingJobs,
		[pendingJobs],
	);
	const processingJobsData = useMemo(
		() => processingJobs?.data || processingJobs,
		[processingJobs],
	);
	const stuckJobsData = useMemo(
		() => stuckJobs?.data || stuckJobs,
		[stuckJobs],
	);
	const failedJobsData = useMemo(
		() => failedJobs?.data || failedJobs,
		[failedJobs],
	);

	// Get failed summary
	const failedSummary = useMemo(() => summary?.failed_summary, [summary]);

	// Series points (5-min rollups; absent on old BE)
	const seriesPoints = useMemo(
		() => summary?.series?.points || [],
		[summary],
	);

	// Queue selector options come from the /stats queues object keys (the 6+
	// names getQueueNames already reports) - never a hardcoded list. A
	// deep-linked queue missing from the payload stays selectable.
	const queueOptions = useMemo(() => {
		const names = Object.keys(summary?.queues || {});
		if (selectedQueue && !names.includes(selectedQueue)) {
			names.push(selectedQueue);
		}
		return names;
	}, [summary, selectedQueue]);

	// Deep-linkable URL writer (contract 14.2): REPLACE, never push - a 30s
	// polling dashboard must not spam the browser history.
	const syncUrlState = useCallback(
		(overrides = {}) => {
			history.replace({
				search: buildUrlSearch({
					tab: activeTab,
					queue: selectedQueue,
					hours: failedWindowHours,
					exception: selectedExceptionClass,
					...overrides,
				}),
			});
		},
		[
			history,
			activeTab,
			selectedQueue,
			failedWindowHours,
			selectedExceptionClass,
		],
	);

	// Failures-by-cause source switch (contract 4.3): try the v2 event-sourced
	// /failures/breakdown first; on a failed probe (old/v1 BE without the
	// route) fall back to /failed/by-exception for this and subsequent cycles.
	// Both payloads flow into the SAME FailuresByCauseTable.
	const loadCauseBreakdown = useCallback(
		(hours, queue) => {
			const params = { hours };
			if (queue) params.queue = queue;
			if (breakdownUnsupportedRef.current) {
				actions.getFailedByException(params);
				return;
			}
			Promise.resolve(actions.getFailuresBreakdown(params))
				.then((response) => {
					// The repository resolves a basis-less { data: null } payload
					// when the route is missing/failed on an old backend.
					if (!response || !response.basis) {
						breakdownUnsupportedRef.current = true;
						actions.getFailedByException(params);
					}
				})
				.catch((error) => {
					console.warn('QueueMonitor: failures/breakdown probe failed', error);
					breakdownUnsupportedRef.current = true;
					actions.getFailedByException(params);
				});
		},
		[actions],
	);

	// The cause table consumes whichever source is live; nullness in the
	// payload (basis / survivorship_warning) carries the difference.
	const causeBreakdownData = useMemo(
		() =>
			failuresBreakdown && failuresBreakdown.basis
				? failuresBreakdown
				: failedByException,
		[failuresBreakdown, failedByException],
	);

	// Initial load of extended stats + retry hotspots + hourly metrics (also
	// refreshed at 60s cadence inside the auto-refresh loop; BE caches make
	// this cheap).
	useEffect(() => {
		actions.getExtendedStats();
		actions.getTopPropertiesByJobs({ limit: 10 });
		actions.getMetricsHourly({ hours: 48 });
		// v3: failure anomalies (trailing 7d) + capacity trend (default 30d).
		actions.getAnomalies({ hours: ANOMALY_WINDOW_HOURS });
		actions.getCapacity({ days: DEFAULT_CAPACITY_DAYS });
		actions.getOutbox();
	}, [actions]);

	// v3: refetch the capacity trend whenever the 30/90-day toggle changes.
	useEffect(() => {
		actions.getCapacity({ days: capacityDays });
	}, [actions, capacityDays]);

	const handleCapacityDaysChange = useCallback((days) => {
		if (!CAPACITY_DAY_OPTIONS.includes(days)) {
			return;
		}
		setCapacityDays(days);
	}, []);

	// Initial load
	useEffect(() => {
		actions.getPendingJobs({
			page: 1,
			per_page: 25,
			queue: selectedQueue,
			property_id: propertyFilterId,
		});
		actions.getSummary();
		actions.getHealth();
	}, [actions, propertyFilterId, selectedQueue]);

	// Failures-by-cause table data (failed tab only; window- and queue-keyed)
	useEffect(() => {
		if (activeTab === 'failed') {
			loadCauseBreakdown(failedWindowHours, selectedQueue);
		}
	}, [loadCauseBreakdown, activeTab, failedWindowHours, selectedQueue]);

	// Auto-refresh
	useEffect(() => {
		if (autoRefresh) {
			const interval = setInterval(() => {
				actions.getSummary();
				actions.getHealth();

				// Extended stats join the loop at 60s cadence (every 2nd tick)
				refreshTickRef.current += 1;
				if (refreshTickRef.current % 2 === 0) {
					actions.getExtendedStats();
					actions.getTopPropertiesByJobs({ limit: 10 });
					actions.getMetricsHourly({ hours: 48 });
					// v3 anomaly + capacity join the 60s cadence.
					actions.getAnomalies({ hours: ANOMALY_WINDOW_HOURS });
					actions.getCapacity({ days: capacityDays });
					// The outbox snapshot is cached 5 min server-side, so 60s is plenty.
					actions.getOutbox();
				}

				// Refresh current tab's data
				switch (activeTab) {
					case 'pending':
						actions.getPendingJobs({
							page: pendingPage,
							per_page: 25,
							queue: selectedQueue,
							property_id: propertyFilterId,
						});
						break;
					case 'processing':
						actions.getProcessingJobs({
							page: processingPage,
							per_page: 25,
							queue: selectedQueue,
							property_id: propertyFilterId,
						});
						break;
					case 'stuck':
						actions.getStuckJobs({
							page: stuckPage,
							per_page: 25,
							queue: selectedQueue,
							property_id: propertyFilterId,
						});
						break;
					case 'failed':
						actions.getFailedJobs({
							page: failedPage,
							per_page: 25,
							hours: failedWindowHours,
							queue: selectedQueue,
							property_id: propertyFilterId,
							exception_class: selectedExceptionClass,
						});
						loadCauseBreakdown(failedWindowHours, selectedQueue);
						break;
					default:
						break;
				}
			}, 30000); // 30 seconds
			return () => clearInterval(interval);
		}
	}, [
		autoRefresh,
		activeTab,
		pendingPage,
		processingPage,
		stuckPage,
		failedPage,
		propertyFilterId,
		failedWindowHours,
		selectedExceptionClass,
		selectedQueue,
		capacityDays,
		actions,
		loadCauseBreakdown,
	]);

	// Load data when tab changes or the property/queue filter changes
	useEffect(() => {
		switch (activeTab) {
			case 'pending':
				actions.getPendingJobs({
					page: pendingPage,
					per_page: 25,
					queue: selectedQueue,
					property_id: propertyFilterId,
				});
				break;
			case 'processing':
				actions.getProcessingJobs({
					page: processingPage,
					per_page: 25,
					queue: selectedQueue,
					property_id: propertyFilterId,
				});
				break;
			case 'stuck':
				actions.getStuckJobs({
					page: stuckPage,
					per_page: 25,
					queue: selectedQueue,
					property_id: propertyFilterId,
				});
				break;
			case 'failed':
				actions.getFailedJobs({
					page: failedPage,
					per_page: 25,
					hours: failedWindowHours,
					queue: selectedQueue,
					property_id: propertyFilterId,
					exception_class: selectedExceptionClass,
				});
				break;
			default:
				break;
		}
	}, [
		actions,
		activeTab,
		failedPage,
		pendingPage,
		processingPage,
		stuckPage,
		propertyFilterId,
		failedWindowHours,
		selectedExceptionClass,
		selectedQueue,
	]);

	// Page change handlers
	const handlePendingPageChange = useCallback(
		(page) => {
			setPendingPage(page);
			actions.getPendingJobs({
				page,
				per_page: 25,
				queue: selectedQueue,
				property_id: propertyFilterId,
			});
		},
		[actions, propertyFilterId, selectedQueue],
	);

	const handleProcessingPageChange = useCallback(
		(page) => {
			setProcessingPage(page);
			actions.getProcessingJobs({
				page,
				per_page: 25,
				queue: selectedQueue,
				property_id: propertyFilterId,
			});
		},
		[actions, propertyFilterId, selectedQueue],
	);

	const handleStuckPageChange = useCallback(
		(page) => {
			setStuckPage(page);
			actions.getStuckJobs({
				page,
				per_page: 25,
				queue: selectedQueue,
				property_id: propertyFilterId,
			});
		},
		[actions, propertyFilterId, selectedQueue],
	);

	const handleFailedPageChange = useCallback(
		(page) => {
			setFailedPage(page);
			actions.getFailedJobs({
				page,
				per_page: 25,
				hours: failedWindowHours,
				queue: selectedQueue,
				property_id: propertyFilterId,
				exception_class: selectedExceptionClass,
			});
		},
		[
			actions,
			propertyFilterId,
			failedWindowHours,
			selectedExceptionClass,
			selectedQueue,
		],
	);

	// Failed-window selector (drives both the by-cause table and the list)
	const handleFailedWindowChange = useCallback(
		(hours) => {
			if (!FAILED_WINDOW_OPTIONS.includes(hours)) {
				return;
			}
			setFailedWindowHours(hours);
			setFailedPage(1);
			syncUrlState({ hours });
		},
		[syncUrlState],
	);

	// Exception-class click-through filter (toggles)
	const handleSelectExceptionClass = useCallback(
		(exceptionClass) => {
			const next =
				selectedExceptionClass === exceptionClass ? null : exceptionClass;
			setSelectedExceptionClass(next);
			setFailedPage(1);
			syncUrlState({ exception: next });
		},
		[selectedExceptionClass, syncUrlState],
	);

	// Shared queue selector (contract 14.1): ONE selection across the four
	// job-list tabs; changing it resets every list back to page 1. On the
	// failed tab it also feeds the breakdown/by-exception calls (effect-keyed).
	const handleQueueChange = useCallback(
		(queue) => {
			setSelectedQueue(queue);
			setPendingPage(1);
			setProcessingPage(1);
			setStuckPage(1);
			setFailedPage(1);
			syncUrlState({ queue });
		},
		[syncUrlState],
	);

	// Health banner reason rows / tiles / tab buttons navigate to a tab
	const handleNavigateTab = useCallback(
		(tab) => {
			if (tab) {
				setActiveTab(tab);
				syncUrlState({ tab });
			}
		},
		[syncUrlState],
	);

	// Property filter handlers
	const handlePropertySelect = useCallback((property) => {
		setSelectedProperty(property);
		// Reset to page 1 when filter changes
		setPendingPage(1);
		setProcessingPage(1);
		setStuckPage(1);
		setFailedPage(1);
	}, []);

	const handleClearPropertyFilter = useCallback(() => {
		setSelectedProperty(null);
		// Reset to page 1 when filter clears
		setPendingPage(1);
		setProcessingPage(1);
		setStuckPage(1);
		setFailedPage(1);
	}, []);

	// Job action handlers
	const handleRetryFailedJob = useCallback(
		async (uuid) => {
			if (window.confirm('Are you sure you want to retry this failed job?')) {
				try {
					await actions.retryFailedJob({ uuid });
					dispatch(
						showToast({
							title: 'Success',
							body: 'Job queued for retry',
							type: 'success',
						}),
					);
					// Refresh trio (contract 5.3): failed list, breakdown, stats.
					actions.getFailedJobs({
						page: failedPage,
						per_page: 25,
						hours: failedWindowHours,
						queue: selectedQueue,
						property_id: propertyFilterId,
						exception_class: selectedExceptionClass,
					});
					loadCauseBreakdown(failedWindowHours, selectedQueue);
					actions.getSummary();
				} catch (error) {
					dispatch(
						showToast({
							title: 'Error',
							body: 'Failed to retry job',
							type: 'error',
						}),
					);
				}
			}
		},
		[
			actions,
			dispatch,
			failedPage,
			propertyFilterId,
			failedWindowHours,
			selectedExceptionClass,
			selectedQueue,
			loadCauseBreakdown,
		],
	);

	// Bulk-retry derivation (contract 5.3). N prefers the event-sourced
	// breakdown's summed retryable_count, else the awaiting-action stock,
	// else the parked list total. TRUE-BOUND RULE: the number RENDERED on the
	// button and in the confirm text is EXACTLY the number SENT as `limit`
	// (min(N, 500)), so the backend can never retry more than the user
	// confirmed.
	const breakdownIsEventSourced = !!(
		failuresBreakdown && failuresBreakdown.basis === 'failed_job_events'
	);

	const bulkRetryCount = useMemo(() => {
		if (breakdownIsEventSourced) {
			return (failuresBreakdown.groups || []).reduce(
				(sum, group) =>
					sum +
					(typeof group.retryable_count === 'number'
						? group.retryable_count
						: 0),
				0,
			);
		}
		if (
			failedSummary?.awaiting_action &&
			typeof failedSummary.awaiting_action.total === 'number'
		) {
			return failedSummary.awaiting_action.total;
		}
		return failedPagination?.total || 0;
	}, [breakdownIsEventSourced, failuresBreakdown, failedSummary, failedPagination]);

	const bulkRetryLimit = Math.min(bulkRetryCount, BULK_RETRY_MAX);

	// Button gate: only v2-BE signals show it (old-BE skew shows no button),
	// and N === 0 hides it.
	const bulkRetryAvailable =
		(!!failedSummary?.awaiting_action || breakdownIsEventSourced) &&
		bulkRetryLimit > 0;

	const handleBulkRetry = useCallback(async () => {
		const limit = Math.min(bulkRetryCount, BULK_RETRY_MAX);
		if (limit < 1) {
			return;
		}
		// Locked confirm text (contract 5.3); {N} === the limit sent below.
		if (
			!window.confirm(
				`Retry up to ${limit} failed jobs matching the current filters? They will re-enter the queue.`,
			)
		) {
			return;
		}
		setIsBulkRetrying(true);
		try {
			const result = await actions.bulkRetryFailedJobs({
				hours: failedWindowHours,
				queue: selectedQueue,
				exceptionClass: selectedExceptionClass,
				limit,
			});
			dispatch(
				showToast({
					title: 'Bulk retry',
					body: `Retried ${
						result && result.retried !== undefined ? result.retried : 0
					} of ${
						result && result.selected !== undefined ? result.selected : 0
					}`,
					type: 'success',
				}),
			);
			// Same refresh trio as the single retry: failed list, breakdown, stats.
			actions.getFailedJobs({
				page: failedPage,
				per_page: 25,
				hours: failedWindowHours,
				queue: selectedQueue,
				property_id: propertyFilterId,
				exception_class: selectedExceptionClass,
			});
			loadCauseBreakdown(failedWindowHours, selectedQueue);
			actions.getSummary();
		} catch (error) {
			console.warn('QueueMonitor: bulk retry failed', error);
			dispatch(
				showToast({
					title: 'Error',
					body: 'Bulk retry failed',
					type: 'error',
				}),
			);
		} finally {
			setIsBulkRetrying(false);
		}
	}, [
		actions,
		dispatch,
		bulkRetryCount,
		failedWindowHours,
		selectedQueue,
		selectedExceptionClass,
		failedPage,
		propertyFilterId,
		loadCauseBreakdown,
	]);

	const handleDeleteFailedJob = useCallback(
		async (uuid) => {
			if (window.confirm('Are you sure you want to delete this failed job?')) {
				try {
					await actions.deleteFailedJob({ uuid });
					dispatch(
						showToast({
							title: 'Success',
							body: 'Failed job deleted',
							type: 'success',
						}),
					);
					actions.getFailedJobs({
						page: failedPage,
						per_page: 25,
						hours: failedWindowHours,
						queue: selectedQueue,
						property_id: propertyFilterId,
						exception_class: selectedExceptionClass,
					});
					actions.getSummary();
				} catch (error) {
					dispatch(
						showToast({
							title: 'Error',
							body: 'Failed to delete job',
							type: 'error',
						}),
					);
				}
			}
		},
		[
			actions,
			dispatch,
			failedPage,
			propertyFilterId,
			failedWindowHours,
			selectedExceptionClass,
			selectedQueue,
		],
	);

	const handleReleaseJob = useCallback(
		async (jobId) => {
			if (window.confirm('Are you sure you want to release this job?')) {
				try {
					await actions.releaseJob({ jobId });
					dispatch(
						showToast({
							title: 'Success',
							body: 'Job released successfully',
							type: 'success',
						}),
					);
					if (activeTab === 'processing') {
						actions.getProcessingJobs({
							page: processingPage,
							per_page: 25,
							queue: selectedQueue,
							property_id: propertyFilterId,
						});
					} else if (activeTab === 'stuck') {
						actions.getStuckJobs({
							page: stuckPage,
							per_page: 25,
							queue: selectedQueue,
							property_id: propertyFilterId,
						});
					}
					actions.getSummary();
				} catch (error) {
					dispatch(
						showToast({
							title: 'Error',
							body: 'Failed to release job',
							type: 'error',
						}),
					);
				}
			}
		},
		[
			actions,
			dispatch,
			activeTab,
			processingPage,
			stuckPage,
			propertyFilterId,
			selectedQueue,
		],
	);

	const handleDeleteJob = useCallback(
		async (jobId) => {
			if (window.confirm('Are you sure you want to delete this job?')) {
				try {
					await actions.deleteJob({ jobId });
					dispatch(
						showToast({
							title: 'Success',
							body: 'Job deleted',
							type: 'success',
						}),
					);
					if (activeTab === 'pending') {
						actions.getPendingJobs({
							page: pendingPage,
							per_page: 25,
							queue: selectedQueue,
							property_id: propertyFilterId,
						});
					} else if (activeTab === 'processing') {
						actions.getProcessingJobs({
							page: processingPage,
							per_page: 25,
							queue: selectedQueue,
							property_id: propertyFilterId,
						});
					} else if (activeTab === 'stuck') {
						actions.getStuckJobs({
							page: stuckPage,
							per_page: 25,
							queue: selectedQueue,
							property_id: propertyFilterId,
						});
					}
					actions.getSummary();
				} catch (error) {
					dispatch(
						showToast({
							title: 'Error',
							body: 'Failed to delete job',
							type: 'error',
						}),
					);
				}
			}
		},
		[
			actions,
			dispatch,
			activeTab,
			pendingPage,
			processingPage,
			stuckPage,
			propertyFilterId,
			selectedQueue,
		],
	);

	const handleRefresh = useCallback(() => {
		actions.getSummary();
		actions.getHealth();
		actions.getExtendedStats();
		actions.getTopPropertiesByJobs({ limit: 10 });
		actions.getMetricsHourly({ hours: 48 });
		// v3 ground-truth panels.
		actions.getAnomalies({ hours: ANOMALY_WINDOW_HOURS });
		actions.getCapacity({ days: capacityDays });
		actions.getOutbox();

		// Refresh current tab
		switch (activeTab) {
			case 'pending':
				actions.getPendingJobs({
					page: pendingPage,
					per_page: 25,
					queue: selectedQueue,
					property_id: propertyFilterId,
				});
				break;
			case 'processing':
				actions.getProcessingJobs({
					page: processingPage,
					per_page: 25,
					queue: selectedQueue,
					property_id: propertyFilterId,
				});
				break;
			case 'stuck':
				actions.getStuckJobs({
					page: stuckPage,
					per_page: 25,
					queue: selectedQueue,
					property_id: propertyFilterId,
				});
				break;
			case 'failed':
				actions.getFailedJobs({
					page: failedPage,
					per_page: 25,
					hours: failedWindowHours,
					queue: selectedQueue,
					property_id: propertyFilterId,
					exception_class: selectedExceptionClass,
				});
				loadCauseBreakdown(failedWindowHours, selectedQueue);
				break;
			default:
				break;
		}
	}, [
		actions,
		activeTab,
		pendingPage,
		processingPage,
		stuckPage,
		failedPage,
		propertyFilterId,
		failedWindowHours,
		selectedExceptionClass,
		selectedQueue,
		capacityDays,
		loadCauseBreakdown,
	]);

	const handleAutoRefreshToggle = useCallback((e) => {
		setAutoRefresh(e.target.checked);
	}, []);

	// Show loading on initial load
	if (isLoadingSummary && !summary) {
		return (
			<AdminLayout>
				<Spinner />
			</AdminLayout>
		);
	}

	return (
		<AdminLayout>
			<div className="queue-monitor">
				<div className="queue-monitor-header">
					<h1>Queue Monitor</h1>
					<div className="queue-monitor-controls">
						<label className="auto-refresh-toggle">
							<input
								type="checkbox"
								checked={autoRefresh}
								onChange={handleAutoRefreshToggle}
							/>
							<span className="ms-2">Auto-refresh (30s)</span>
						</label>
						<button
							onClick={handleRefresh}
							className="btn btn-sm btn-primary ms-3"
							disabled={isLoadingSummary}
						>
							{isLoadingSummary ? 'Refreshing...' : 'Refresh Now'}
						</button>
					</div>
				</div>

				<HealthBanner health={health} onNavigate={handleNavigateTab} />
				{/* v3: ground-truth worker liveness; renders nothing on a pre-v3
				    backend (health.metrics.workers absent). */}
				<WorkersPanel workers={health?.metrics?.workers} />
				<ThroughputStrip
					throughput={summary?.throughput}
					ticks={summary?.ticks}
					seriesPoints={seriesPoints}
				/>
				<StatsGrid summary={summary} onNavigateTab={handleNavigateTab} />
				<ExtendedStatsGrid
					extendedStats={extendedStats}
					topPropertiesByJobs={topPropertiesByJobs}
					isLoadingTopProperties={isLoadingTopPropertiesByJobs}
					metricsHourly={metricsHourly}
					isLoadingMetricsHourly={isLoadingMetricsHourly}
					capacity={capacity}
					isLoadingCapacity={isLoadingCapacity}
					capacityDays={capacityDays}
					onCapacityDaysChange={handleCapacityDaysChange}
				/>

				{/* Property Address Filter - Show only on data tabs */}
				{JOB_LIST_TABS.includes(activeTab) && (
					<PropertyAddressFilter
						value={selectedProperty}
						onPropertySelect={handlePropertySelect}
						onClear={handleClearPropertyFilter}
						disabled={isLoadingSummary}
						authToken={authToken}
						dispatch={dispatch}
					/>
				)}

				<div className="queue-tabs mb-3">
					<button
						className={`queue-tab ${activeTab === 'overview' ? 'active' : ''}`}
						onClick={() => handleNavigateTab('overview')}
					>
						Overview
					</button>
					<button
						className={`queue-tab ${activeTab === 'pending' ? 'active' : ''}`}
						onClick={() => handleNavigateTab('pending')}
					>
						Pending Jobs ({pendingPagination?.total || 0})
					</button>
					<button
						className={`queue-tab ${activeTab === 'processing' ? 'active' : ''}`}
						onClick={() => handleNavigateTab('processing')}
					>
						Processing Jobs ({processingPagination?.total || 0})
					</button>
					<button
						className={`queue-tab ${activeTab === 'stuck' ? 'active' : ''}`}
						onClick={() => handleNavigateTab('stuck')}
					>
						Stuck Jobs ({stuckPagination?.total || 0})
					</button>
					<button
						className={`queue-tab ${activeTab === 'failed' ? 'active' : ''}`}
						onClick={() => handleNavigateTab('failed')}
					>
						Failed Jobs ({failedSummary?.total_failed || 0})
					</button>
					{outbox && (
						<button
							className={`queue-tab ${activeTab === 'outbox' ? 'active' : ''}`}
							onClick={() => handleNavigateTab('outbox')}
						>
							Email Outbox ({outbox.alert_stall?.waiting_users || 0} waiting)
						</button>
					)}
				</div>

				{/* Shared queue selector (contract 14.1): one selection across the
				    four job-list tabs; options come from the /stats queues keys. */}
				{JOB_LIST_TABS.includes(activeTab) && (
					<div className="queue-filter-row mb-3 d-flex align-items-center">
						<label
							htmlFor="queue-monitor-queue-filter"
							className="me-2 mb-0 fw-semibold"
						>
							Queue
						</label>
						<select
							id="queue-monitor-queue-filter"
							aria-label="Filter jobs by queue"
							className="form-select form-select-sm w-auto"
							value={selectedQueue || ''}
							onChange={(e) => handleQueueChange(e.target.value || null)}
						>
							<option value="">All queues</option>
							{queueOptions.map((name) => (
								<option key={name} value={name}>
									{name}
								</option>
							))}
						</select>
					</div>
				)}

				<div className="queue-tab-content">
					{activeTab === 'overview' && summary && (
						<PerQueueTable
							queues={summary.queues || {}}
							byQueue24h={failedSummary?.by_queue_24h_utc}
							seriesPoints={seriesPoints}
						/>
					)}

					{/* A failed /outbox poll hides the tab; say so instead of a blank area */}
					{activeTab === 'outbox' &&
						(outbox ? (
							<EmailOutboxPanel outbox={outbox} />
						) : (
							<p className="text-muted">Outbox health unavailable</p>
						))}

					{activeTab === 'pending' && (
						<PendingTab
							jobs={pendingJobsData}
							pagination={pendingPagination}
							onPageChange={handlePendingPageChange}
							onDelete={handleDeleteJob}
							isLoading={isLoadingPendingJobs}
							onSelectProperty={handlePropertySelect}
						/>
					)}

					{activeTab === 'processing' && (
						<ProcessingTab
							jobs={processingJobsData}
							pagination={processingPagination}
							onPageChange={handleProcessingPageChange}
							onRelease={handleReleaseJob}
							onDelete={handleDeleteJob}
							isLoading={isLoadingProcessingJobs}
							onSelectProperty={handlePropertySelect}
						/>
					)}

					{activeTab === 'stuck' && (
						<StuckTab
							jobs={stuckJobsData}
							pagination={stuckPagination}
							onPageChange={handleStuckPageChange}
							onRelease={handleReleaseJob}
							onDelete={handleDeleteJob}
							isLoading={isLoadingStuckJobs}
							onSelectProperty={handlePropertySelect}
						/>
					)}

					{activeTab === 'failed' && (
						<>
							{/* v3: per-hour failure bars with anomaly highlighting;
							    renders nothing on a pre-v3 backend (anomalies null). */}
							<FailureAnomalyChart data={anomalies} />
							<FailuresByCauseTable
								data={causeBreakdownData}
								windowHours={failedWindowHours}
								onWindowChange={handleFailedWindowChange}
								selectedExceptionClass={selectedExceptionClass}
								onSelectExceptionClass={handleSelectExceptionClass}
								isLoading={
									!!(isLoadingFailuresBreakdown || isLoadingFailedByException)
								}
								bulkRetry={
									bulkRetryAvailable
										? {
												label: `Retry all (${bulkRetryLimit} retryable)`,
												onClick: handleBulkRetry,
												disabled: isBulkRetrying,
											}
										: null
								}
							/>
							<FailedTab
								jobs={failedJobsData}
								pagination={failedPagination}
								onPageChange={handleFailedPageChange}
								onRetry={handleRetryFailedJob}
								onDelete={handleDeleteFailedJob}
								isLoading={isLoadingFailedJobs}
								failedSummary={failedSummary}
								onSelectProperty={handlePropertySelect}
								selectedExceptionClass={selectedExceptionClass}
							/>
						</>
					)}
				</div>
			</div>
		</AdminLayout>
	);
};

export default memo(QueueMonitor);
